import React, { createContext, useContext } from 'react'
import { Box, CircularProgress, InputAdornment, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/Search'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'

/**
 * LibraryListParts.jsx — story 167 (HU-04). The pieces every family of the library is made of: a tile, the tray a
 * process unfolds into, a badge, a search box and a list that scrolls (and asks for more at its end, D12).
 *
 * The look (PO, 29/09/2026: no more invisible rows with a coloured bar on the left): each piece is a small card
 * with its family's glyph on a tinted tile, its facts on one line and its version or figure as a pill on the
 * right. A process that is unfolded OPENS into a tray under its card — same outline, dashed seam — and its rows
 * hang from a rail with elbows, so what is inside the process reads as inside it.
 */
/** How far a nested tray moves its rail to the right of the tray it lives in. */
export const LIBRARY_ROW_INDENT_PX = 18

/** The colour of the family a row belongs to (D4: families told apart at a glance), set by LibraryFamilies. */
export const LibraryAccent = createContext('primary.main')

/** 'success.main' → the colour of the theme it names. */
export const paletteColorOf = (theme, path) => path.split('.').reduce((node, key) => node?.[key], theme.palette) || theme.palette.primary.main
const SCROLL_END_MARGIN_PX = 48
const RAIL_LEFT_PX = 7
const RAIL_WIDTH_PX = 2
const ROW_GAP_PX = 4
// Where the elbow meets a nested row: about its middle, the same for every row of every tray.
const ELBOW_TOP_PX = 19

/** What travels in the drag, as today: strings only, and an absent value simply does not travel (IN16). */
export const writeDragData = (event, dragData) => {
  Object.entries(dragData).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return
    }
    event.dataTransfer.setData(key, String(value))
  })
  event.dataTransfer.effectAllowed = 'move'
}

const useAccentColor = () => {
  const accent = useContext(LibraryAccent)
  return (theme) => paletteColorOf(theme, accent)
}

/**
 * PO, 29/09/2026: each family keeps its colour and the edges stay soft. What is chosen does not shine: it is RAISED —
 * a surface one step lighter with a soft dark shadow under it, as if it came up off the list — and its name turns to
 * the green of MIC. No coloured outline and no glow.
 */
const quiet = (theme, opacity) => alpha(theme.palette.common.white, opacity)
export const raisedSurfaceOf = (theme) => ({
  backgroundImage: 'linear-gradient(' + quiet(theme, 0.06) + ', ' + quiet(theme, 0.06) + ')',
  boxShadow: '0 2px 6px ' + alpha(theme.palette.common.black, 0.45)
})

/**
 * The family's glyph on a tinted tile: the piece says what it is before its name is read. `tip` says in words what the
 * glyph means (PO, 29/09/2026: two jobs of a process with different glyphs and nothing saying why).
 */
export const LibraryGlyph = ({ Icon, small = false, tip = '', testId }) => {
  const colorOf = useAccentColor()
  const size = small ? 22 : 28
  const glyph = (
    <Box
      component='span'
      data-testid={testId}
      aria-hidden={tip ? undefined : true}
      aria-label={tip || undefined}
      sx={(theme) => {
        const color = colorOf(theme)
        return {
          width: size,
          height: size,
          flex: '0 0 auto',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: small ? '7px' : '9px',
          color,
          bgcolor: alpha(color, 0.14),
          boxShadow: 'inset 0 0 0 1px ' + alpha(color, 0.22),
          '& svg': { fontSize: small ? 13 : 16 }
        }
      }}
    >
      <Icon />
    </Box>
  )
  if (!tip) {
    return glyph
  }
  // The empty title keeps the card's own native title (its name) from showing on top of this tooltip.
  return (
    <Box component='span' title='' sx={{ display: 'inline-flex', flex: '0 0 auto' }}>
      <Tooltip title={tip} placement='top' disableInteractive>{glyph}</Tooltip>
    </Box>
  )
}

const BADGE_TONES = ['primary', 'success', 'warning', 'error', 'info']

/**
 * Story 184 (CA2 · RN-02.4, RN-02.5, R1): a badge with `onClick` is a control of its own inside a row that is
 * itself clickable and draggable. It swallows the click (the row must NOT get chosen), answers to Enter and
 * Space, and cancels the native `dragstart` that would otherwise bubble up to the draggable row. Without
 * `onClick` nothing of this applies and the badge is the plain span it always was.
 */
const interactiveBadgeProps = (onClick) => (onClick
  ? {
      role: 'button',
      tabIndex: 0,
      onClick: (event) => { event.stopPropagation(); onClick(event) },
      onKeyDown: (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          event.stopPropagation()
          onClick(event)
        }
      },
      onDragStart: (event) => { event.preventDefault(); event.stopPropagation() }
    }
  : {})

/** A fact about the piece that is not its identity — present already, a newer version, unreadable… */
export const LibraryBadge = ({ testId, tone = 'neutral', icon = null, onClick, children, ...rest }) => (
  <Box
    component='span'
    data-testid={testId}
    data-tone={tone}
    {...rest}
    {...interactiveBadgeProps(onClick)}
    sx={(theme) => {
      const color = BADGE_TONES.includes(tone) ? theme.palette[tone].main : theme.palette.text.secondary
      return {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.4,
        maxWidth: '100%',
        px: 0.75,
        py: 0.1,
        borderRadius: '999px',
        fontSize: 11,
        lineHeight: 1.5,
        fontWeight: 500,
        color,
        bgcolor: alpha(color, 0.12),
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '& svg': { fontSize: 12 },
        // PO (01/10/2026, user testing #184): a badge that can be pressed must LOOK pressable — the MIC outlined
        // control, exactly like the `vN ▾` of the row (solid border, transparent background, 4px corners).
        ...(onClick
          ? {
              cursor: 'pointer',
              bgcolor: 'transparent',
              border: '1px solid ' + color,
              borderRadius: theme.shape.borderRadius + 'px',
              px: 0.75,
              py: 0,
              fontSize: 11.5,
              lineHeight: 1.7,
              '&:hover': { bgcolor: alpha(color, 0.08) },
              '&:focus-visible': { outline: '2px solid ' + alpha(color, 0.6), outlineOffset: 1 }
            }
          : {})
      }
    }}
  >
    {icon}
    {children}
  </Box>
)

/**
 * The look of a card of the panel — a piece of the library or a row of its tree (PO, 29/09/2026: one language for
 * the whole panel). `color` is the colour of what the card is; chosen it takes a ring of it, open its bottom goes
 * square so the tray under it reads as part of it, nested it is the smaller card of a tray.
 */
export const libraryCardSx = (theme, { color, chosen = false, open = false, nested = false, draggable = false, disabled = false }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  py: nested ? 0.5 : 0.75,
  px: 0.75,
  borderRadius: open ? '10px 10px 0 0' : nested ? '8px' : '10px',
  border: '1px solid',
  borderColor: chosen ? quiet(theme, 0.16) : open ? alpha(color, 0.22) : quiet(theme, nested ? 0.06 : 0.09),
  bgcolor: nested ? alpha(theme.palette.common.black, 0.18) : theme.palette.background.card,
  boxShadow: 'none',
  ...(chosen ? raisedSurfaceOf(theme) : {}),
  cursor: draggable ? 'grab' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  userSelect: 'none',
  transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow', 'transform'], { duration: theme.transitions.duration.shortest }),
  '& .library-row-grip': { opacity: 0, transition: 'opacity 120ms' },
  '&:hover': {
    borderColor: alpha(color, 0.35),
    transform: draggable ? 'translateY(-1px)' : 'none'
  },
  '&:hover .library-row-grip': { opacity: 0.8 },
  '&:active': { cursor: draggable ? 'grabbing' : 'pointer', transform: 'none' },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } }
})

/** The pill on the right of a card: a version, or the kind of a row of the tree. */
export const LibraryChip = ({ testId, children }) => {
  const colorOf = useAccentColor()
  return (
    <Box
      component='span'
      data-testid={testId}
      sx={(theme) => {
        const color = colorOf(theme)
        return {
          flex: '0 0 auto',
          px: 0.75,
          borderRadius: '999px',
          border: '1px solid ' + alpha(color, 0.3),
          color,
          fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
          fontSize: 11,
          lineHeight: 1.6,
          fontVariantNumeric: 'tabular-nums'
        }
      }}
    >
      {children}
    </Box>
  )
}

/**
 * One piece of a family. `nested` makes it the smaller card of a tray; `open` squares its bottom so the tray of what
 * it contains reads as part of it. `facts` are what the piece is made of (its technology, how many operations…),
 * shown as quiet pills; `children` are the badges of what is happening to it (already in this workflow…).
 */
export const LibraryRow = ({
  testId,
  secondaryTestId,
  primary,
  secondary = '',
  icon = null,
  iconTip = '',
  iconTestId,
  chip = '',
  chipTestId,
  facts = [],
  nested = false,
  open = false,
  chosen = false,
  draggable = false,
  disabled = false,
  onClick,
  onDragStart,
  onDragEnd,
  leading,
  trailing,
  children
}) => {
  const colorOf = useAccentColor()
  const badges = [
    // #184 CA2: a fact may carry a `tone` and an `onClick` (the usage badge); without them it is the quiet pill of always.
    ...facts.filter((fact) => fact.text).map(({ key, testId: factTestId, text, tone, onClick: onFactClick }) => <LibraryBadge key={'fact-' + key} testId={factTestId} tone={tone} onClick={onFactClick}>{text}</LibraryBadge>),
    ...React.Children.toArray(children).filter(Boolean)
  ]
  return (
    <Box
      data-testid={testId}
      draggable={draggable}
      aria-selected={chosen}
      title={secondary ? primary + ' · ' + secondary : primary}
      onClick={onClick}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      sx={(theme) => libraryCardSx(theme, { color: colorOf(theme), chosen, open, nested, draggable, disabled })}
    >
      <Stack direction='row' alignItems='center' spacing={0.75} sx={{ minWidth: 0 }}>
        {leading}
        {icon ? <LibraryGlyph Icon={icon} small={nested} tip={iconTip} testId={iconTestId} /> : null}
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, color: chosen ? 'primary.main' : undefined, transition: 'color 120ms' }}>{primary}</Typography>
          {secondary
            ? <Typography noWrap data-testid={secondaryTestId} sx={{ fontSize: 11.5, lineHeight: 1.35, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{secondary}</Typography>
            : null}
          {/* Under the text, not under the arrow: the pills line up with what they describe. */}
          {badges.length > 0
            ? <Stack direction='row' flexWrap='wrap' useFlexGap spacing={0.5} sx={{ minWidth: 0, mt: 0.5 }}>{badges}</Stack>
            : null}
        </Box>
        {chip ? <LibraryChip testId={chipTestId}>{chip}</LibraryChip> : null}
        {trailing}
        {draggable ? <DragIndicatorIcon className='library-row-grip' sx={{ flex: '0 0 auto', fontSize: 16, color: 'text.disabled', ml: -0.25, mr: -0.5 }} /> : null}
      </Stack>
    </Box>
  )
}

/**
 * What an unfolded process contains, attached under its card: same outline, a dashed seam, a faint wash of the
 * family colour and a rail its items hang from. A tray inside a tray is a sub-process.
 */
export const LibraryTray = ({ testId, header = null, nested = false, children }) => {
  const colorOf = useAccentColor()
  return (
    <Box
      data-testid={testId}
      sx={(theme) => {
        const color = colorOf(theme)
        return {
          mt: '-1px',
          px: 0.75,
          py: 0.75,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          borderRadius: nested ? '0 0 8px 8px' : '0 0 10px 10px',
          border: '1px solid ' + alpha(color, 0.22),
          borderTop: '1px dashed ' + alpha(color, 0.22),
          // No wash of the family's colour inside: the edge is enough to say whose tray it is (PO, 29/09/2026).
          bgcolor: quiet(theme, nested ? 0.03 : 0.02)
        }
      }}
    >
      {header}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: ROW_GAP_PX / 8, pl: LIBRARY_ROW_INDENT_PX + 'px' }}>
        {children}
      </Box>
    </Box>
  )
}

/**
 * One thing hung from the rail of a tray: a row, and the tray it opens into when it is a sub-process. The rail runs
 * down through every item but the last, where it turns into the elbow — so where a branch ends is drawn.
 */
export const LibraryTrayItem = ({ last = false, children }) => {
  const colorOf = useAccentColor()
  return (
    <Box
      sx={(theme) => {
        const rail = RAIL_WIDTH_PX + 'px solid ' + alpha(colorOf(theme), 0.3)
        const railLeft = -(LIBRARY_ROW_INDENT_PX - RAIL_LEFT_PX)
        return {
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: railLeft,
            top: -ROW_GAP_PX,
            height: last ? ELBOW_TOP_PX + ROW_GAP_PX : 'calc(100% + ' + ROW_GAP_PX + 'px)',
            borderLeft: rail,
            borderBottom: last ? rail : 'none',
            borderBottomLeftRadius: last ? 7 : 0,
            width: last ? LIBRARY_ROW_INDENT_PX - RAIL_LEFT_PX : 0,
            pointerEvents: 'none'
          },
          '&::after': last
            ? {}
            : {
                content: '""',
                position: 'absolute',
                left: railLeft,
                top: ELBOW_TOP_PX,
                width: LIBRARY_ROW_INDENT_PX - RAIL_LEFT_PX,
                borderTop: rail,
                pointerEvents: 'none'
              }
        }
      }}
    >
      {children}
    </Box>
  )
}

export const LibrarySearchField = ({ testId, value, onChange, placeholder }) => (
  <TextField
    size='small'
    fullWidth
    value={value}
    onChange={(event) => onChange(event.target.value)}
    placeholder={placeholder}
    inputProps={{ 'data-testid': testId, 'aria-label': placeholder }}
    InputProps={{ startAdornment: <InputAdornment position='start'><SearchIcon fontSize='small' /></InputAdornment> }}
    sx={{ px: 1, '& .MuiInputBase-root': { borderRadius: '999px' }, '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
  />
)

/**
 * RN-10.3 / RN-11.1: the rows, and around them what the list has to say — a failure (not the empty list),
 * "Not available" when the list really is empty (the text of today), the loading circle and, at the end, the
 * failure of the next page (E-14).
 */
export const LibraryScrollList = ({
  testId,
  loading = false,
  failed = false,
  failureText = '',
  empty = false,
  pageFailed = false,
  pageFailureText = '',
  onReachEnd = () => {},
  children
}) => {
  const { t_ } = useSmartTranslation()
  return (
    <Box
      data-testid={testId}
      onScroll={(event) => {
        const list = event.currentTarget
        if (list.scrollTop + list.clientHeight >= list.scrollHeight - SCROLL_END_MARGIN_PX) {
          onReachEnd()
        }
      }}
      sx={{ flex: '1 1 0', minHeight: 0, overflowY: 'auto', overflowX: 'hidden', px: 1, pt: 0.25, pb: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}
    >
      {failed ? <Typography data-testid={testId + '-failed'} variant='body2' color='error'>{failureText}</Typography> : null}
      {!failed && !loading && empty
        ? (
          <Typography
            data-testid={testId + '-empty'}
            variant='body2'
            color='text.secondary'
            sx={{ px: 1, py: 1.5, textAlign: 'center', border: 1, borderStyle: 'dashed', borderColor: 'divider', borderRadius: '10px' }}
          >
            {t_('Not available')}
          </Typography>
          )
        : null}
      {children}
      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={18} data-testid={testId + '-loading'} /></Box> : null}
      {pageFailed ? <Typography data-testid={testId + '-page-failed'} variant='body2' color='error'>{pageFailureText}</Typography> : null}
    </Box>
  )
}
